import { Fragment, useRef, useState } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import { useAuth } from '../auth/ContextoAuth'
import { catalogo } from '../api/servicios'
import { cantidad as formatearCantidad, dinero } from '../api/formato'
import type { Categoria, Producto, TipoProducto, UnidadMedida } from '../api/tipos'
import { Dialogo } from '../componentes/Dialogo'
import { EscanerCamara, hayCamaraPosible } from '../componentes/EscanerCamara'
import { Estado } from '../componentes/Estado'
import { Paginador, usarRetardo } from '../componentes/Paginador'
import { mensajeDeError, usarCargar } from '../componentes/usarCargar'
import './listados.css'

type Ventana =
    | { tipo: 'ninguno' }
    | { tipo: 'nuevo' }
    | { tipo: 'editar'; producto: Producto }
    | { tipo: 'precio'; producto: Producto }
    | { tipo: 'codigo'; producto: Producto }
    | { tipo: 'imagen'; producto: Producto }
    | { tipo: 'categoria' }

export function Productos() {
    const { puede } = useAuth()
    const puedeEditar = puede('catalogo.editar')
    const puedeCambiarPrecio = puede('precio.cambiar')

    const [texto, setTexto] = useState('')
    const [categoriaId, setCategoriaId] = useState('')
    const [estadoFiltro, setEstadoFiltro] = useState<'activos' | 'inactivos' | 'todos'>('activos')
    const [pagina, setPagina] = useState(1)
    const [ventana, setVentana] = useState<Ventana>({ tipo: 'ninguno' })

    const busqueda = usarRetardo(texto)

    const categorias = usarCargar(() => catalogo.categorias(false), [])

    const productos = usarCargar(
        () =>
            catalogo.productos({
                texto: busqueda || undefined,
                categoriaId: categoriaId ? Number(categoriaId) : undefined,
                activo: estadoFiltro === 'todos' ? undefined : estadoFiltro === 'activos',
                pagina,
            }),
        [busqueda, categoriaId, estadoFiltro, pagina],
    )

    // Cualquier cambio de filtro vuelve a la primera pagina: quedarse en la
    // pagina 4 de un resultado que ahora tiene una sola es un listado vacio sin
    // explicacion.
    function filtrar(accion: () => void) {
        accion()
        setPagina(1)
    }

    function refrescar() {
        setVentana({ tipo: 'ninguno' })
        productos.recargar()
    }

    return (
        <div className="pagina-listado columna g4">
            <header className="fila entre envuelve g3">
                <h1>Productos</h1>

                {puedeEditar && (
                    <div className="fila envuelve g2">
                        <button
                            type="button"
                            className="boton"
                            onClick={() => setVentana({ tipo: 'categoria' })}
                        >
                            Nueva categoría
                        </button>
                        <button
                            type="button"
                            className="boton boton-primario"
                            onClick={() => setVentana({ tipo: 'nuevo' })}
                        >
                            Nuevo producto
                        </button>
                    </div>
                )}
            </header>

            <section className="panel columna g3">
                <div className="filtros">
                    <input
                        className="campo"
                        type="search"
                        placeholder="Buscar por nombre, SKU o código de barras"
                        value={texto}
                        onChange={(e) => filtrar(() => setTexto(e.target.value))}
                    />

                    <select
                        className="campo"
                        aria-label="Categoría"
                        value={categoriaId}
                        onChange={(e) => filtrar(() => setCategoriaId(e.target.value))}
                    >
                        <option value="">Todas las categorías</option>
                        {(categorias.datos ?? []).map((c) => (
                            <option key={c.categoriaId} value={c.categoriaId}>
                                {c.nombre}
                            </option>
                        ))}
                    </select>

                    <select
                        className="campo"
                        aria-label="Estado"
                        value={estadoFiltro}
                        onChange={(e) =>
                            filtrar(() =>
                                setEstadoFiltro(e.target.value as 'activos' | 'inactivos' | 'todos'),
                            )
                        }
                    >
                        <option value="activos">Activos</option>
                        <option value="inactivos">Inactivos</option>
                        <option value="todos">Todos</option>
                    </select>
                </div>

                <Estado
                    cargando={productos.cargando}
                    error={productos.error}
                    alReintentar={productos.recargar}
                    vacio={(productos.datos?.items ?? []).length === 0}
                    mensajeVacio="Ningún producto coincide con la búsqueda."
                >
                    <div className="tabla-envoltura">
                        <table className="tabla">
                            <thead>
                                <tr>
                                    <th aria-label="Foto" />
                                    <th>Producto</th>
                                    <th className="oculto-angosto">Categoría</th>
                                    <th className="derecha">Precio</th>
                                    <th className="derecha oculto-angosto">Stock mín.</th>
                                    <th className="oculto-angosto" />
                                </tr>
                            </thead>
                            <tbody>
                                {(productos.datos?.items ?? []).map((p) => {
                                    const acciones = (
                                        <div className="fila g2 acciones-fila">
                                            {puedeCambiarPrecio && (
                                                <button
                                                    type="button"
                                                    className="boton boton-fantasma chico"
                                                    onClick={() =>
                                                        setVentana({ tipo: 'precio', producto: p })
                                                    }
                                                >
                                                    Precio
                                                </button>
                                            )}
                                            {puedeEditar && (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="boton boton-fantasma chico"
                                                        onClick={() =>
                                                            setVentana({ tipo: 'imagen', producto: p })
                                                        }
                                                    >
                                                        Foto
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="boton boton-fantasma chico"
                                                        onClick={() =>
                                                            setVentana({ tipo: 'codigo', producto: p })
                                                        }
                                                    >
                                                        Código
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="boton boton-fantasma chico"
                                                        onClick={() =>
                                                            setVentana({ tipo: 'editar', producto: p })
                                                        }
                                                    >
                                                        Editar
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )

                                    return (
                                        <Fragment key={p.productoId}>
                                            <tr>
                                                <td>
                                                    <Miniatura producto={p} />
                                                </td>
                                                <td>
                                                    <div className="columna">
                                                        <span>{p.nombre}</span>
                                                        <span className="tenue diminuto numero">
                                                            {p.sku} · {p.unidadMedidaCodigo}
                                                            {p.tipo === 'Pesable' && ' · pesable'}
                                                            {!p.activo && ' · inactivo'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="oculto-angosto">{p.categoriaNombre}</td>
                                                <td className="derecha numero">{dinero(p.precioVigente)}</td>
                                                <td className="derecha numero oculto-angosto">
                                                    {formatearCantidad(p.stockMinimo, p.decimales)}
                                                </td>
                                                <td className="derecha oculto-angosto">{acciones}</td>
                                            </tr>

                                            {/* En el telefono las acciones van debajo de su producto:
                                                a la derecha empujaban la tabla fuera de la pantalla y
                                                obligaban a desplazar en horizontal para tocarlas. */}
                                            <tr className="fila-acciones">
                                                <td colSpan={6}>{acciones}</td>
                                            </tr>
                                        </Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </Estado>

                <Paginador pagina={productos.datos} alCambiar={setPagina} />
            </section>

            {(ventana.tipo === 'nuevo' || ventana.tipo === 'editar') && (
                <DialogoProducto
                    producto={ventana.tipo === 'editar' ? ventana.producto : null}
                    categorias={categorias.datos ?? []}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}

            {ventana.tipo === 'precio' && (
                <DialogoPrecio
                    producto={ventana.producto}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}

            {ventana.tipo === 'imagen' && (
                <DialogoImagen
                    producto={ventana.producto}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}

            {ventana.tipo === 'codigo' && (
                <DialogoCodigo
                    producto={ventana.producto}
                    alGuardar={refrescar}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}

            {ventana.tipo === 'categoria' && (
                <DialogoCategoria
                    categorias={categorias.datos ?? []}
                    alGuardar={() => {
                        setVentana({ tipo: 'ninguno' })
                        categorias.recargar()
                    }}
                    alCerrar={() => setVentana({ tipo: 'ninguno' })}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */
/* Alta y edicion                                                              */
/* -------------------------------------------------------------------------- */

interface PropsProducto {
    producto: Producto | null
    categorias: Categoria[]
    alGuardar: () => void
    alCerrar: () => void
}

function DialogoProducto({ producto, categorias, alGuardar, alCerrar }: PropsProducto) {
    const editando = producto !== null
    const unidades = usarCargar<UnidadMedida[]>(
        () => (editando ? Promise.resolve([]) : catalogo.unidades()),
        [editando],
    )

    const [sku, setSku] = useState(producto?.sku ?? '')
    const [nombre, setNombre] = useState(producto?.nombre ?? '')
    const [descripcion, setDescripcion] = useState(producto?.descripcion ?? '')
    const [categoriaId, setCategoriaId] = useState(
        producto ? String(producto.categoriaId) : '',
    )
    const [unidadId, setUnidadId] = useState('')
    const [tipo, setTipo] = useState<TipoProducto>(producto?.tipo ?? 'Unitario')
    const [stockMinimo, setStockMinimo] = useState(String(producto?.stockMinimo ?? 0))
    const [precio, setPrecio] = useState('')
    const [codigoBarra, setCodigoBarra] = useState('')
    const [activo, setActivo] = useState(producto?.activo ?? true)
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (nombre.trim().length === 0) {
            setError('El nombre es obligatorio.')
            return
        }

        if (!categoriaId) {
            setError('Elija una categoría.')
            return
        }

        const minimo = Number(stockMinimo || 0)

        if (!Number.isFinite(minimo) || minimo < 0) {
            setError('El stock mínimo no puede ser negativo.')
            return
        }

        setEnviando(true)

        try {
            if (producto) {
                await catalogo.actualizarProducto(producto.productoId, {
                    nombre: nombre.trim(),
                    descripcion: descripcion.trim() || undefined,
                    categoriaId: Number(categoriaId),
                    stockMinimo: minimo,
                    activo,
                })
            } else {
                const valor = Number(precio)

                if (!Number.isFinite(valor) || valor < 1) {
                    setError('Ingrese el precio de venta: sin precio vigente no se puede vender.')
                    setEnviando(false)
                    return
                }

                if (!unidadId) {
                    setError('Elija la unidad de medida.')
                    setEnviando(false)
                    return
                }

                await catalogo.crearProducto({
                    sku: sku.trim(),
                    nombre: nombre.trim(),
                    categoriaId: Number(categoriaId),
                    unidadMedidaId: Number(unidadId),
                    tipo,
                    stockMinimo: minimo,
                    precio: Math.round(valor),
                    codigoBarra: codigoBarra.trim() || undefined,
                })
            }

            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo={editando ? 'Editar producto' : 'Nuevo producto'}
            alCerrar={alCerrar}
            ancho="ancho"
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-producto"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Guardar'}
                    </button>
                </>
            }
        >
            <form id="forma-producto" className="columna g4" onSubmit={guardar}>
                {!editando && (
                    <div>
                        <label className="etiqueta" htmlFor="sku">
                            SKU
                        </label>
                        <input
                            id="sku"
                            className="campo"
                            maxLength={40}
                            placeholder="ARR-1KG"
                            value={sku}
                            onChange={(e) => setSku(e.target.value)}
                        />
                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                            Código interno. Letras, números, punto, guion y guion bajo.
                        </p>
                    </div>
                )}

                <div>
                    <label className="etiqueta" htmlFor="nombre">
                        Nombre
                    </label>
                    <input
                        id="nombre"
                        className="campo"
                        maxLength={160}
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="categoria-producto">
                        Categoría
                    </label>
                    <select
                        id="categoria-producto"
                        className="campo"
                        value={categoriaId}
                        onChange={(e) => setCategoriaId(e.target.value)}
                    >
                        <option value="">Elija una categoría…</option>
                        {categorias
                            .filter((c) => c.activa || String(c.categoriaId) === categoriaId)
                            .map((c) => (
                                <option key={c.categoriaId} value={c.categoriaId}>
                                    {c.nombre}
                                </option>
                            ))}
                    </select>
                </div>

                {!editando && (
                    <>
                        <div>
                            <label className="etiqueta" htmlFor="unidad">
                                Unidad de medida
                            </label>
                            <Estado
                                cargando={unidades.cargando}
                                error={unidades.error}
                                alReintentar={unidades.recargar}
                            >
                                <select
                                    id="unidad"
                                    className="campo"
                                    value={unidadId}
                                    onChange={(e) => {
                                        setUnidadId(e.target.value)

                                        // La unidad decide si el producto se
                                        // vende por peso: elegir KG y dejarlo
                                        // como unitario es el error tipico.
                                        const elegida = (unidades.datos ?? []).find(
                                            (u) => String(u.unidadMedidaId) === e.target.value,
                                        )

                                        if (elegida) {
                                            setTipo(elegida.decimales > 0 ? 'Pesable' : 'Unitario')
                                        }
                                    }}
                                >
                                    <option value="">Elija la unidad…</option>
                                    {(unidades.datos ?? []).map((u) => (
                                        <option key={u.unidadMedidaId} value={u.unidadMedidaId}>
                                            {u.nombre} ({u.codigo})
                                        </option>
                                    ))}
                                </select>
                            </Estado>
                        </div>

                        <div>
                            <span className="etiqueta">Tipo</span>
                            <div className="grupo-opciones">
                                {(['Unitario', 'Pesable'] as TipoProducto[]).map((opcion) => (
                                    <button
                                        key={opcion}
                                        type="button"
                                        className={`boton${tipo === opcion ? ' boton-primario' : ''}`}
                                        aria-pressed={tipo === opcion}
                                        onClick={() => setTipo(opcion)}
                                    >
                                        {opcion === 'Unitario' ? 'Por unidad' : 'Por peso'}
                                    </button>
                                ))}
                            </div>
                            <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                                {tipo === 'Pesable'
                                    ? 'En el POS se pedirá el peso a mano.'
                                    : 'En el POS se agrega de a una unidad.'}
                            </p>
                        </div>

                        <div>
                            <label className="etiqueta" htmlFor="precio-inicial">
                                Precio de venta
                            </label>
                            <input
                                id="precio-inicial"
                                className="campo campo-numero"
                                type="number"
                                min={1}
                                step={1}
                                inputMode="numeric"
                                value={precio}
                                onChange={(e) => setPrecio(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="etiqueta" htmlFor="codigo-inicial">
                                Código de barras <span className="tenue">(opcional)</span>
                            </label>
                            <input
                                id="codigo-inicial"
                                className="campo"
                                maxLength={80}
                                value={codigoBarra}
                                onChange={(e) => setCodigoBarra(e.target.value)}
                            />
                        </div>
                    </>
                )}

                <div>
                    <label className="etiqueta" htmlFor="stock-minimo">
                        Stock mínimo
                    </label>
                    <input
                        id="stock-minimo"
                        className="campo campo-numero"
                        type="number"
                        min={0}
                        step="any"
                        inputMode="decimal"
                        value={stockMinimo}
                        onChange={(e) => setStockMinimo(e.target.value)}
                    />
                    <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                        Debajo de esta cantidad el producto aparece en el listado de reposición.
                    </p>
                </div>

                {editando && (
                    <>
                        <div>
                            <label className="etiqueta" htmlFor="descripcion">
                                Descripción <span className="tenue">(opcional)</span>
                            </label>
                            <input
                                id="descripcion"
                                className="campo"
                                maxLength={500}
                                value={descripcion}
                                onChange={(e) => setDescripcion(e.target.value)}
                            />
                        </div>

                        <label className="fila g2 interruptor">
                            <input
                                type="checkbox"
                                checked={activo}
                                onChange={(e) => setActivo(e.target.checked)}
                            />
                            <span>Activo</span>
                        </label>
                        <p className="tenue diminuto">
                            Un producto inactivo no se puede vender, pero sigue en el historial.
                        </p>
                    </>
                )}

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Precio                                                                      */
/* -------------------------------------------------------------------------- */

function DialogoPrecio({
    producto,
    alGuardar,
    alCerrar,
}: {
    producto: Producto
    alGuardar: () => void
    alCerrar: () => void
}) {
    const [precio, setPrecio] = useState(String(producto.precioVigente ?? ''))
    const [motivo, setMotivo] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        const valor = Number(precio)

        if (!Number.isFinite(valor) || valor < 1) {
            setError('Ingrese un precio mayor que cero.')
            return
        }

        if (motivo.trim().length === 0) {
            setError('El motivo es obligatorio: queda en la auditoría.')
            return
        }

        setEnviando(true)

        try {
            await catalogo.cambiarPrecio(producto.productoId, Math.round(valor), motivo.trim())
            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Cambiar precio"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-precio"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Guardando…' : 'Guardar'}
                    </button>
                </>
            }
        >
            <form id="forma-precio" className="columna g4" onSubmit={guardar}>
                <div className="columna g1">
                    <strong>{producto.nombre}</strong>
                    <span className="tenue chico numero">
                        Precio vigente: {dinero(producto.precioVigente)}
                    </span>
                </div>

                <div>
                    <label className="etiqueta" htmlFor="precio-nuevo">
                        Precio nuevo
                    </label>
                    <input
                        id="precio-nuevo"
                        className="campo campo-numero"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        value={precio}
                        onChange={(e) => setPrecio(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="motivo-precio">
                        Motivo
                    </label>
                    <input
                        id="motivo-precio"
                        className="campo"
                        maxLength={250}
                        placeholder="Alza del proveedor, promoción, corrección…"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                    />
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Codigos de barra                                                            */
/* -------------------------------------------------------------------------- */

function DialogoCodigo({
    producto,
    alGuardar,
    alCerrar,
}: {
    producto: Producto
    alGuardar: () => void
    alCerrar: () => void
}) {
    const [codigo, setCodigo] = useState('')
    const [principal, setPrincipal] = useState(producto.codigosBarra.length === 0)
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)
    const [camara, setCamara] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (codigo.trim().length === 0) {
            setError('Escanee o escriba el código.')
            return
        }

        setEnviando(true)

        try {
            await catalogo.agregarCodigoBarra(producto.productoId, codigo.trim(), principal)
            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    // El escaner **reemplaza** a este dialogo en lugar de abrirse encima. Dos
    // dialogos superpuestos se disputan el foco y una sola pulsacion de Escape
    // cierra los dos, que es justo lo que no quiere alguien a mitad de cargar
    // codigos. Como el componente sigue montado, lo ya tecleado no se pierde.
    if (camara) {
        return (
            <EscanerCamara
                modo="unico"
                titulo={`Código de ${producto.nombre}`}
                alLeer={(leido) => {
                    setCodigo(leido)
                    setError(null)
                }}
                alCerrar={() => setCamara(false)}
            />
        )
    }

    return (
        <Dialogo
            titulo="Códigos de barras"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cerrar
                    </button>
                    <button
                        type="submit"
                        form="forma-codigo"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Agregando…' : 'Agregar'}
                    </button>
                </>
            }
        >
            <form id="forma-codigo" className="columna g4" onSubmit={guardar}>
                <div className="columna g1">
                    <strong>{producto.nombre}</strong>
                    {producto.codigosBarra.length === 0 ? (
                        <span className="tenue chico">Sin códigos registrados.</span>
                    ) : (
                        <ul className="lista-simple">
                            {producto.codigosBarra.map((c) => (
                                <li key={c.productoCodigoBarraId} className="fila entre g2">
                                    <span className="numero chico">{c.codigo}</span>
                                    <span className="tenue diminuto">
                                        {c.principal ? 'principal' : ''}
                                        {!c.activo ? ' · inactivo' : ''}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div>
                    <label className="etiqueta" htmlFor="codigo-nuevo">
                        Código nuevo
                    </label>
                    <div className="fila g2">
                        <input
                            id="codigo-nuevo"
                            className="campo numero crece"
                            maxLength={80}
                            autoComplete="off"
                            placeholder="Escanee el producto"
                            value={codigo}
                            onChange={(e) => setCodigo(e.target.value)}
                        />

                        {/* La camara escribe en este mismo campo: de ahi para
                            abajo es identico a pasarlo con la pistola. */}
                        {hayCamaraPosible() && (
                            <button
                                type="button"
                                className="boton"
                                onClick={() => setCamara(true)}
                                title="Escanear con la cámara"
                            >
                                Cámara
                            </button>
                        )}
                    </div>
                </div>

                <label className="fila g2 interruptor">
                    <input
                        type="checkbox"
                        checked={principal}
                        onChange={(e) => setPrincipal(e.target.checked)}
                    />
                    <span>Marcar como principal</span>
                </label>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Categorias                                                                  */
/* -------------------------------------------------------------------------- */

function DialogoCategoria({
    categorias,
    alGuardar,
    alCerrar,
}: {
    categorias: Categoria[]
    alGuardar: () => void
    alCerrar: () => void
}) {
    const [nombre, setNombre] = useState('')
    const [padreId, setPadreId] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [enviando, setEnviando] = useState(false)

    async function guardar(evento: FormEvent) {
        evento.preventDefault()
        setError(null)

        if (nombre.trim().length === 0) {
            setError('El nombre es obligatorio.')
            return
        }

        setEnviando(true)

        try {
            await catalogo.crearCategoria(
                nombre.trim(),
                padreId ? Number(padreId) : undefined,
            )
            alGuardar()
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setEnviando(false)
        }
    }

    return (
        <Dialogo
            titulo="Nueva categoría"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="forma-categoria"
                        className="boton boton-primario"
                        disabled={enviando}
                    >
                        {enviando ? 'Creando…' : 'Crear'}
                    </button>
                </>
            }
        >
            <form id="forma-categoria" className="columna g4" onSubmit={guardar}>
                <div>
                    <label className="etiqueta" htmlFor="nombre-categoria">
                        Nombre
                    </label>
                    <input
                        id="nombre-categoria"
                        className="campo"
                        maxLength={100}
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                    />
                </div>

                <div>
                    <label className="etiqueta" htmlFor="padre-categoria">
                        Categoría padre <span className="tenue">(opcional)</span>
                    </label>
                    <select
                        id="padre-categoria"
                        className="campo"
                        value={padreId}
                        onChange={(e) => setPadreId(e.target.value)}
                    >
                        <option value="">Sin categoría padre</option>
                        {categorias
                            .filter((c) => c.activa)
                            .map((c) => (
                                <option key={c.categoriaId} value={c.categoriaId}>
                                    {c.nombre}
                                </option>
                            ))}
                    </select>
                </div>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </form>
        </Dialogo>
    )
}

/* -------------------------------------------------------------------------- */
/* Imagen                                                                      */
/* -------------------------------------------------------------------------- */

function Miniatura({ producto }: { producto: Producto }) {
    const [fallo, setFallo] = useState(false)

    if (producto.imagenUrl === null || fallo) {
        return <span className="miniatura miniatura-vacia" aria-hidden="true" />
    }

    return (
        <img
            className="miniatura"
            src={producto.imagenUrl}
            alt=""
            loading="lazy"
            onError={() => setFallo(true)}
        />
    )
}

const TIPOS_ADMITIDOS = 'image/jpeg,image/png,image/webp'

function DialogoImagen({
    producto,
    alGuardar,
    alCerrar,
}: {
    producto: Producto
    alGuardar: () => void
    alCerrar: () => void
}) {
    // Se trabaja sobre una copia local para poder mostrar el resultado de la
    // carga sin cerrar el dialogo ni recargar el listado entero.
    const [actual, setActual] = useState(producto)
    const [error, setError] = useState<string | null>(null)
    const [ocupado, setOcupado] = useState(false)
    const [huboCambios, setHuboCambios] = useState(false)

    const selector = useRef<HTMLInputElement>(null)

    async function subir(evento: ChangeEvent<HTMLInputElement>) {
        const archivo = evento.target.files?.[0]

        // El input se limpia siempre: si no, elegir el mismo archivo dos veces
        // seguidas no dispara el evento y parece que el boton no anda.
        evento.target.value = ''

        if (!archivo) return

        setError(null)
        setOcupado(true)

        try {
            setActual(await catalogo.subirImagen(producto.productoId, archivo))
            setHuboCambios(true)
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setOcupado(false)
        }
    }

    async function quitar() {
        setError(null)
        setOcupado(true)

        try {
            setActual(await catalogo.quitarImagen(producto.productoId))
            setHuboCambios(true)
        } catch (fallo) {
            setError(mensajeDeError(fallo))
        } finally {
            setOcupado(false)
        }
    }

    return (
        <Dialogo
            titulo="Foto del producto"
            alCerrar={huboCambios ? alGuardar : alCerrar}
            acciones={
                <button
                    type="button"
                    className="boton boton-primario"
                    onClick={huboCambios ? alGuardar : alCerrar}
                >
                    Listo
                </button>
            }
        >
            <div className="columna g4">
                <div className="columna g1">
                    <strong>{actual.nombre}</strong>
                    <span className="tenue chico numero">{actual.sku}</span>
                </div>

                <div className="vista-previa">
                    {actual.imagenUrl ? (
                        <img src={actual.imagenUrl} alt={`Foto de ${actual.nombre}`} />
                    ) : (
                        <span className="tenue chico">Sin foto</span>
                    )}
                </div>

                <input
                    ref={selector}
                    type="file"
                    accept={TIPOS_ADMITIDOS}
                    hidden
                    onChange={(e) => void subir(e)}
                />

                <div className="fila envuelve g2">
                    <button
                        type="button"
                        className="boton crece"
                        disabled={ocupado}
                        onClick={() => selector.current?.click()}
                    >
                        {ocupado ? 'Subiendo…' : actual.imagenUrl ? 'Cambiar foto' : 'Elegir foto'}
                    </button>

                    {actual.imagenUrl && (
                        <button
                            type="button"
                            className="boton boton-peligro crece"
                            disabled={ocupado}
                            onClick={() => void quitar()}
                        >
                            Quitar
                        </button>
                    )}
                </div>

                <p className="tenue diminuto">
                    JPG, PNG o WEBP, hasta 2 MB. Se muestra en la grilla del POS, así que
                    conviene una foto cuadrada y del producto solo, sin fondo cargado.
                </p>

                {error && (
                    <div className="aviso aviso-error" role="alert">
                        {error}
                    </div>
                )}
            </div>
        </Dialogo>
    )
}
